import {adminUrlBuilder} from "@app/api/utils";
import type {
  IAiVoicePreferences,
  IAnalyticsAiChatRequest,
  IAnalyticsAiChatResponse,
  IAnalyticsAiInsightsResponse,
} from "@torim-app/types";

export interface CustomerCardData {
  customerId: string;
  fullName: string;
  phoneNumber: string;
  nextAppointment: {
    date: string;
    time: string;
    services: string;
  } | null;
}

export interface AgentMessage {
  role: 'user' | 'assistant';
  content: string;
  customerCard?: CustomerCardData;
  timestamp?: number;
}

interface ChatResponse {
  reply: string;
  tokensUsed: number;
}

interface VoiceChatResponse {
  transcribedText: string;
  reply: string;
  tokensUsed: number;
}

export class AiApi {
  static readonly path = 'ai';

  static async sendMessage(message: string, conversationHistory: AgentMessage[]): Promise<ChatResponse> {
    const url = adminUrlBuilder({}, `${AiApi.path}/chat`);
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify({message, conversationHistory})
    });
    if (!response.ok)
      throw "Failed to send message to AI agent";

    return await response.json();
  }

  static async sendVoice(audioBlob: Blob, conversationHistory: AgentMessage[]): Promise<VoiceChatResponse> {
    const url = adminUrlBuilder({}, `${AiApi.path}/voice`);
    const formData = new FormData();
    // Use correct file extension based on mime type (MediaRecorder produces webm/mp4/ogg)
    const ext = audioBlob.type.includes('webm') ? 'webm'
      : audioBlob.type.includes('mp4') ? 'mp4'
      : audioBlob.type.includes('ogg') ? 'ogg'
      : 'wav';
    formData.append('audio', audioBlob, `recording.${ext}`);
    formData.append('conversationHistory', JSON.stringify(conversationHistory));

    const response = await fetch(url, {
      method: 'post',
      body: formData
    });
    if (!response.ok)
      throw "Failed to send voice to AI agent";

    return await response.json();
  }

  static async getLiveToken(): Promise<{ token: string }> {
    const url = adminUrlBuilder({}, `${AiApi.path}/live-token`);
    const response = await fetch(url, {
      method: 'post',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({}),
    });
    if (!response.ok) throw 'Failed to get live token';
    return await response.json();
  }

  static async getVoicePreferences(): Promise<{ preferences: IAiVoicePreferences | null }> {
    const url = adminUrlBuilder({}, `${AiApi.path}/voice-preferences`);
    const response = await fetch(url);
    if (!response.ok) throw 'Failed to get voice preferences';
    return await response.json();
  }

  static async setVoicePreferences(preferences: IAiVoicePreferences): Promise<void> {
    const url = adminUrlBuilder({}, `${AiApi.path}/voice-preferences`);
    const response = await fetch(url, {
      method: 'post',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(preferences),
    });
    if (!response.ok) throw 'Failed to save voice preferences';
  }

  // Gemini calls can hang — without a timeout the thinking indicator spins forever
  private static readonly ANALYTICS_AI_TIMEOUT_MS = 45_000;

  // Always the last 30 days — computed once a day per business and cached server-side
  static async getAnalyticsInsights(): Promise<IAnalyticsAiInsightsResponse> {
    const url = adminUrlBuilder({}, `${AiApi.path}/analytics-insights`);
    const response = await fetch(url, {
      method: 'post',
      headers: {'content-type': 'application/json'},
      body: JSON.stringify({}),
      signal: AbortSignal.timeout(AiApi.ANALYTICS_AI_TIMEOUT_MS)
    });
    if (!response.ok)
      throw "Failed to get analytics AI insights";

    return await response.json();
  }

  static async sendAnalyticsChatMessage(params: IAnalyticsAiChatRequest): Promise<IAnalyticsAiChatResponse> {
    const url = adminUrlBuilder({}, `${AiApi.path}/analytics-chat`);
    const response = await fetch(url, {
      method: 'post',
      headers: {'content-type': 'application/json'},
      body: JSON.stringify(params),
      signal: AbortSignal.timeout(AiApi.ANALYTICS_AI_TIMEOUT_MS)
    });
    if (!response.ok)
      throw "Failed to send analytics AI chat message";

    return await response.json();
  }

  static async textToSpeech(text: string): Promise<Blob> {
    const url = adminUrlBuilder({}, `${AiApi.path}/tts`);
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify({text})
    });
    if (!response.ok)
      throw "Failed to generate speech";

    return await response.blob();
  }
}
